import type Spinnies from 'spinnies';

import { CONSOLE_ICONS } from '../../constants/common';
import type { CodemodOptions } from '../types';
import { assessPrerequisitesBatch } from './dependencyChecks';

interface ClaudePrereqHandlerOptions {
  options: CodemodOptions;
  spinners: Spinnies;
  codemodPath: string;
}

export function handleClaudePrereqs({
  options,
  codemodPath,
  spinners,
}: ClaudePrereqHandlerOptions) {
  spinners.add('prerequisite-check', { text: 'Checking prerequisites...' });
  // Fail-fast: check prerequisites for all target paths up-front
  const { allPassed, packageRootToTargets, failedPackageRoots } = assessPrerequisitesBatch(
    options.targetPaths,
    codemodPath,
    spinners,
  );
  if (!allPassed) {
    spinners.add('prerequisite-partial-failure', {
      text: 'One or more packages failed prerequisite checks - these targets will be skipped:',
      status: 'fail',
    });

    for (const failedRoot of failedPackageRoots) {
      const targets = packageRootToTargets.get(failedRoot) ?? [];
      // Filter out targets that belong to failed package roots
      // eslint-disable-next-line no-param-reassign
      options.targetPaths = options.targetPaths.filter(
        (targetPath) => !targets.includes(targetPath),
      );
      spinners.add(`prerequisite-fail-${failedRoot}`, {
        text: `- \x1b[2m\x1b[31m${failedRoot}\x1b[0m${targets.length > 0 && targets[0] !== failedRoot ? ` (targets: ${targets.join(', ')})` : ''}\x1b[0m`,
        indent: 2,
        status: 'non-spinnable',
      });
    }
  }

  if (!options.targetPaths.length) {
    spinners.add('no-valid-targets', {
      text: `${CONSOLE_ICONS.error} No valid target paths remaining after prerequisite checks - exiting.`,
      status: 'fail',
    });
    spinners.stopAll('fail');
    spinners.checkIfActiveSpinners();
    throw new Error('No valid target paths remaining after prerequisite checks');
  }
}
